import { useEffect, useState } from "react";

import { useParams } from "react-router-dom";

import { TCourse } from "../../../../../@types";
import { apis, callApi } from "../../../../../api";
import { useAccount } from "../../../../../hooks/useAccount";

export type TPreviewCourseService = {
	listeners: (() => void)[];
	savedCourse: TCourse | null;
	owned: boolean;
	addEventListener: (cb: () => void) => void;
	saveCourse: (course: TCourse, owned: boolean) => void;
	clearData: () => void;
	emitChanges: () => void;
	removeEventListener: (cb: () => void) => void;
};

export const PreviewCourseService: TPreviewCourseService = {
	listeners: [],
	savedCourse: null,
	owned: false,

	addEventListener(cb) {
		this.listeners = this.listeners.filter((listener) => listener !== cb);
		this.listeners.push(cb);
	},

	saveCourse(course, owned) {
		this.savedCourse = course;
		this.owned = owned;
		this.emitChanges();
	},

	clearData() {
		this.savedCourse = null;
		this.emitChanges();
	},

	/**
	 * Send new course data to all listeners
	 */
	emitChanges() {
		for (const listener of this.listeners) {
			listener();
		}
	},

	removeEventListener(cb) {
		this.listeners = this.listeners.filter((listener) => listener !== cb);
	},
};

/**
 * This hook should call only once
 */
export function usePreviewCourseInit() {
	const params = useParams();
	const slug = params.slug ?? "";
	const { account } = useAccount();

	const [loading, setLoading] = useState(true);

	useEffect(() => {
		const getCourseBySlug = async () => {
			setLoading(true);
			try {
				let url = "";
				if (account) {
					url = `${apis.GET_COURSE_DETAIL}/${slug}`;
				} else {
					url = `${apis.PREVIEW_COURSE_BY_SLUG}/${slug}`;
				}

				const response = await callApi({
					method: "GET",
					url,
				});

				if (!response?.course) {
					return;
				}

				PreviewCourseService.saveCourse(response.course, response.owned);
			} catch (error: any) {
			} finally {
				setLoading(false);
			}
		};
		getCourseBySlug();
	}, [account, slug]);

	useEffect(() => {
		return () => {
			PreviewCourseService.clearData();
		};
	}, []);

	return { loading };
}

export function usePreviewCourse() {
	const [course, setCourse] = useState(PreviewCourseService.savedCourse);
	const [owned, setOwned] = useState(PreviewCourseService.owned);

	useEffect(() => {
		const updateCourse = () => {
			setCourse(PreviewCourseService.savedCourse);
			setOwned(PreviewCourseService.owned);
		};

		PreviewCourseService.addEventListener(updateCourse);

		return () => {
			PreviewCourseService.removeEventListener(updateCourse);
		};
	}, []);

	return { course, owned };
}
